'use client';

/**
 * Navbar Component
 * ================
 *
 * A responsive navigation bar:
 * - Desktop: Fixed top navbar with liquid glass effect
 * - Mobile: Simple glass header with hide-on-scroll behavior
 *
 * SEO CONSIDERATIONS:
 * - Uses semantic <nav> element
 * - Links use Next.js Link component for client-side navigation
 * - Logo links to home page
 */

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import GlassSurface from '@/components/ui/GlassSurface';

interface NavLink {
  href: string;
  label: string;
}

const navLinks: NavLink[] = [
  { href: '/', label: 'Home' },
  { href: '/gallery', label: 'Gallery' },
  { href: '/amenities', label: 'Amenities' },
  { href: '/layouts', label: 'Layouts' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
];

const Navbar: React.FC = () => {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileHeaderVisible, setIsMobileHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Check if current path matches the link (for active state on desktop)
  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(href);
  };

  // Hide/show mobile header on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when scrolling up or at top
      if (currentScrollY < lastScrollY.current || currentScrollY < 50) {
        setIsMobileHeaderVisible(true);
      } else {
        // Hide header when scrolling down
        setIsMobileHeaderVisible(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Always show header when menu is open
      setIsMobileHeaderVisible(true);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Handle mobile menu link click
  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Desktop Navigation - hidden on mobile */}
      <nav
        className="hidden md:block fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[85vw]"
        role="navigation"
        aria-label="Desktop navigation"
      >
        <GlassSurface
          width="100%"
          height={67}
          borderRadius={34}
          borderWidth={0.07}
          brightness={50}
          opacity={0.93}
          blur={11}
          displace={0}
          backgroundOpacity={0}
          saturation={1}
          distortionScale={-180}
          redOffset={0}
          greenOffset={10}
          blueOffset={20}
        >
          <div className="flex items-center justify-between w-full px-6">
            {/* Logo on the left */}
            <Link href="/" className="flex-shrink-0">
              <Image
                src="/assets/LOGOS/magante_regalia_gold_logo.svg"
                alt="Magnate Regalia Logo"
                width={150}
                height={45}
                loading="eager"
                className="h-10 w-auto"
              />
            </Link>

            {/* Nav links on the right */}
            <ul className="flex items-center gap-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className={`nav-link-liquid px-4 py-2 text-[15px] font-semibold tracking-wide transition-all duration-300 ${
                      isActive(link.href)
                        ? 'nav-link-active text-white'
                        : 'text-white/95 hover:text-white'
                    }`}
                    aria-current={isActive(link.href) ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </GlassSurface>
      </nav>

      {/* Mobile Header - Glass bar with logo + hamburger */}
      <header
        className={`md:hidden fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
          isMobileHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        {/* Glass background bar */}
        <div className="mx-2 mt-3 px-3 py-2.5 rounded-2xl bg-black/30 backdrop-blur-xl border border-white/10 shadow-lg">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <Image
                src="/assets/LOGOS/magante_regalia_gold_logo.svg"
                alt="Magnate Regalia Logo"
                width={100}
                height={32}
                loading="eager"
                className="h-7 w-auto"
              />
            </Link>

            {/* Hamburger Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white active:scale-95 active:bg-white/20 transition-all"
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Fullscreen Overlay Menu - Simple & Clean */}
      <div
        className={`fixed inset-0 z-[60] bg-black/95 backdrop-blur-md transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Close Button */}
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="absolute top-6 right-6 z-20 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white active:bg-white/20 transition-all"
          aria-label="Close menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Simple Menu Links */}
        <nav
          className="w-full h-full flex flex-col items-center justify-center gap-2"
          role="navigation"
          aria-label="Mobile navigation"
        >
          {navLinks.map((link, index) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={handleMobileLinkClick}
              className={`text-3xl font-display font-semibold py-3 px-8 rounded-full transition-all duration-200 ${
                isActive(link.href)
                  ? 'text-amber-400 bg-amber-400/10'
                  : 'text-white/90 active:text-amber-400 active:bg-white/5'
              }`}
              style={{
                transitionDelay: isMobileMenuOpen ? `${index * 50}ms` : '0ms',
                transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
                opacity: isMobileMenuOpen ? 1 : 0,
              }}
            >
              {link.label}
            </Link>
          ))}

          {/* Contact CTA Button */}
          <Link
            href="/contact"
            onClick={handleMobileLinkClick}
            className="mt-8 px-8 py-3 bg-amber-400 text-slate-900 font-semibold rounded-full active:scale-95 transition-all"
            style={{
              transitionDelay: isMobileMenuOpen ? `${navLinks.length * 50}ms` : '0ms',
              transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(20px)',
              opacity: isMobileMenuOpen ? 1 : 0,
            }}
          >
            Schedule Visit
          </Link>
        </nav>
      </div>
    </>
  );
};

export default Navbar;